import { ConnectionListResult, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionTableDownstreamMetricsFilteredByColumnQuery,
  VirtualConnectionTableDownstreamMetricsFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamMetricsConnection_nodes,
  VirtualConnectionTableDownstreamMetricsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamMetricsFilteredByColumnQuery';
import {
  VirtualConnectionTableDownstreamMetricsQuery,
  VirtualConnectionTableDownstreamMetricsQuery_virtualConnectionTables_downstreamMetricsConnection_nodes,
  VirtualConnectionTableDownstreamMetricsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamMetricsQuery';
import { MetricSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  VirtualConnectionTableDownstreamMetricsFilteredByColumnQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamMetricsFilteredByColumnQueryGql';
import {
  VirtualConnectionTableDownstreamMetricsQueryGql,
} from '../../queries/VirtualConnectionTableDownstreamMetricsQueryGql';
import { getRowDataForMetric, MetricsDisplayGrid, MetricsDisplayGridRowData } from '../sharedDisplayGrid/MetricsDisplayGrid';
import { MetricsValidSortFields } from '../sorting/sortProviders/MetricsGridSortProvider';

type QueryVariables = VirtualConnectionTableDownstreamMetricsQueryVariables;
type QueryResults = VirtualConnectionTableDownstreamMetricsQuery;
type QueryResultListNode = VirtualConnectionTableDownstreamMetricsQuery_virtualConnectionTables_downstreamMetricsConnection_nodes;
type GridRowData = MetricsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionTableDownstreamMetricsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = VirtualConnectionTableDownstreamMetricsFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionTableDownstreamMetricsFilteredByColumnQuery_virtualConnectionTables_columnsConnection_nodes_downstreamMetricsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  return virtualConnection ? virtualConnection.downstreamMetricsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnectionTables && resultData.virtualConnectionTables[0];
  const virtualConnectionTableColumns = virtualConnection && virtualConnection.columnsConnection && virtualConnection.columnsConnection.nodes;
  const column = virtualConnectionTableColumns && virtualConnectionTableColumns[0];
  return column ? column.downstreamMetricsConnection : null;
}

export type VirtualConnectionTableDownstreamMetricsQueryGridProps =
  GridSortProps<MetricsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionTableDownstreamMetricsQueryGrid(props: VirtualConnectionTableDownstreamMetricsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamMetricsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredMetricsLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamMetricsFilteredByColumnQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedColumnId: props.filteredColumnId,
        orderBy: props.orderBy as MetricSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForMetric}
    >
      {({ rowStore }) => <MetricsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionTableDownstreamMetricsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as MetricSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForMetric}
    >
      {({ rowStore }) => <MetricsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
